import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import {
  networkID,
  sumWeiToEther,
  findLowestWeiToEther,
  compareWei,
} from "../../../../static/utils";
import { getNetworkCurrency, DEFAULT_CURRENCY } from "../../../../static/Constants";

// Theme & Common Components
import NavBar from "../../../Components/NavBar";
import { colors } from "../../../../theme";

// Subcomponents
import {
  AllMissileHero,
  AllMissileCard,
  AllMissileFilters,
  AllMissileEmptyState,
  AllMissileSkeleton,
  MissileHowToPlay,
} from "./components";

import "./AllMissilelotteryStyle.css";

const withTimeout = (promise, ms = 4500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const AllMissilelottery = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [networkId, setNetworkId] = useState(networkID);
  const [lotteryList, setLotteryList] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  // Filters & Sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId]
  );

  /**
   * Fetch All Live & Active Missile (Type 3) Lotteries
   */
  const fetchMissileLotteries = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Safety watchdog to guarantee loading finishes and never gets stuck
      const watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7000);

      try {
        if (window.ethereum) {
          window.web3 = web3;
          if (window.ethereum.networkVersion) {
            setNetworkId(window.ethereum.networkVersion);
          } else if (web3 && web3.eth && web3.eth.net && web3.eth.net.getId) {
            withTimeout(web3.eth.net.getId(), 2000)
              .then((netId) => {
                if (netId) setNetworkId(netId);
              })
              .catch(() => {});
          }
        }

        // 1. Get total lottery IDs count
        const totalIdsRaw = await withTimeout(
          cinstance.methods.lotteryId().call(),
          4000
        ).catch((err) => {
          console.warn("[RocketBET] lotteryId() call failed:", err);
          return 0;
        });

        const totalIds = Number(totalIdsRaw || 0);
        const lotteryCalls = [];

        // In blockchain contract, lotteries are indexed from 1 up to totalIds
        for (let i = 1; i <= totalIds; i++) {
          lotteryCalls.push(
            withTimeout(cinstance.methods.lottery(i).call(), 4000).catch((err) => {
              console.log("lottery", i, err);
              return null;
            })
          );
        }

        const lotteryResults = await Promise.all(lotteryCalls);

        // Filter strictly for Missile / RocketBET lotteries (Type 3) with active status (0, 3, or 5)
        const filteredLotteries = lotteryResults
          .filter(
            (response) =>
              response &&
              (response.status === "0" ||
                response.status === "3" ||
                response.status === "5") &&
              Number(response.lotteryType) === 3
          )
          .sort((a, b) => Number(b.lotteryId) - Number(a.lotteryId));

        setLotteryList(filteredLotteries);

        // 2. Fetch partner metadata for unique partner addresses
        const uniquePartners = [
          ...new Set(
            filteredLotteries
              .map((item) => item.partnerAddress || item.partner)
              .filter(
                (addr) =>
                  addr &&
                  addr !== "0x0000000000000000000000000000000000000000"
              )
          ),
        ];

        if (uniquePartners.length > 0) {
          Promise.all(
            uniquePartners.map((addr) =>
              withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3500)
                .then((res) => (res ? Object.assign({ address: addr }, res) : null))
                .catch(() => null)
            )
          ).then((partnerResults) => {
            const map = {};
            partnerResults.filter(Boolean).forEach((p) => {
              map[p.address] = p;
              if (p.partnerAddress) map[p.partnerAddress] = p;
            });
            setPartnerMap(map);
          });
        }

        if (isRefresh) {
          toast.success("Missile lotteries refreshed!");
        }
      } catch (err) {
        console.error("Error loading missile lotteries:", err);
        toast.error("Failed to load on-chain missile lotteries.");
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  // Load Partner Logos (Optional S3 API)
  useEffect(() => {
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          const partnerData =
            response && response.data && response.data.result
              ? response.data.result
              : response?.data || [];
          setPartnerLogoData(Array.isArray(partnerData) ? partnerData : []);
        })
        .catch(() => {
          setPartnerLogoData([]);
        });
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchMissileLotteries();
  }, [fetchMissileLotteries]);

  /**
   * Computed Stats for Hero Banner
   */
  const { totalActive, totalPrizePool, lowestEntryFee } = useMemo(() => {
    const prizeList = [];
    const entryFees = [];

    lotteryList.forEach((item) => {
      if (item.totalPrize) prizeList.push(item.totalPrize);
      if (item.entryFee) entryFees.push(item.entryFee);
    });

    return {
      totalActive: lotteryList.length,
      totalPrizePool: sumWeiToEther(prizeList, 5),
      lowestEntryFee: findLowestWeiToEther(entryFees, 5),
    };
  }, [lotteryList]);

  /**
   * Filter and Sort Lotteries for Grid Display
   */
  const filteredLotteries = useMemo(() => {
    return lotteryList
      .filter((lottery) => {
        // Search Filter
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const idMatch = String(lottery.lotteryId).toLowerCase().includes(q);
          const ownerMatch = (lottery.ownerAddress || "").toLowerCase().includes(q);
          const partnerAddr = (
            lottery.partner ||
            lottery.partnerAddress ||
            ""
          ).toLowerCase();
          const partnerMatch = partnerAddr.includes(q);

          const partnerInfo =
            partnerMap[lottery.partnerAddress] ||
            partnerMap[lottery.partner] ||
            {};
          const nameMatch = (partnerInfo.name || "").toLowerCase().includes(q);
          const companyMatch = (partnerInfo.companyName || "")
            .toLowerCase()
            .includes(q);

          if (!idMatch && !ownerMatch && !partnerMatch && !nameMatch && !companyMatch) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const idA = Number(a.lotteryId || 0);
        const idB = Number(b.lotteryId || 0);
        const prizeComp = compareWei(a.totalPrize, b.totalPrize);
        const feeComp = compareWei(a.entryFee, b.entryFee);
        const capA = Number(a.capacity || 0);
        const capB = Number(b.capacity || 0);

        if (sortBy === "oldest") return idA - idB;
        if (sortBy === "highestPrize") return -prizeComp || idB - idA;
        if (sortBy === "lowestEntry") return feeComp || idA - idB;
        if (sortBy === "capacityHigh") return capB - capA || idB - idA;
        if (sortBy === "capacityLow") return capA - capB || idA - idB;
        // Default: newest first
        return idB - idA;
      });
  }, [lotteryList, searchQuery, sortBy, partnerMap]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setSortBy("newest");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || sortBy !== "newest";

  return (
    <Box
      className="missile-page-container"
      sx={{ bgcolor: colors.dashboard.canvas }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <NavBar />

      <Container
        maxWidth="xl"
        sx={{
          pt: { xs: "96px", sm: "108px", md: "116px" },
          pb: 6,
          flexGrow: 1,
        }}
      >
        {/* 1. Hero Section with Live Stats */}
        <AllMissileHero
          totalActive={totalActive}
          combinedPrize={totalPrizePool}
          lowestEntry={lowestEntryFee}
          currency={currency}
        />

        {/* 2. Search, Filter & Sort Controls */}
        <AllMissileFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onRefresh={() => fetchMissileLotteries(true)}
          loading={refreshing}
        />

        {/* 3. Loading State (Skeletons) */}
        {loading && <AllMissileSkeleton count={6} />}

        {/* 4. Empty State (Never gets stuck on loader) */}
        {!loading && filteredLotteries.length === 0 && (
          <AllMissileEmptyState
            onReset={handleResetFilters}
            onRefresh={() => fetchMissileLotteries(true)}
            hasActiveFilters={hasActiveFilters}
          />
        )}

        {/* 5. Live Missile Lottery Cards Grid */}
        {!loading && filteredLotteries.length > 0 && (
          <Grid container spacing={3}>
            {filteredLotteries.map((item) => {
              const partnerAddr = item.partnerAddress || item.partner;
              const partnerData = partnerMap[partnerAddr] || null;

              return (
                <Grid item xs={12} sm={6} lg={4} key={item.lotteryId}>
                  <AllMissileCard
                    lottery={item}
                    partner={partnerData}
                    partnerLogo={partnerLogoData}
                    currency={currency}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* 6. Educational Guide: Tiers & How to Play */}
        <MissileHowToPlay />
      </Container>
    </Box>
  );
};

export default AllMissilelottery;
